/**
 * size-option-logger.js
 *
 * Diagnostic logger for variant-picker option groups whose title contains
 * "Size" (case-insensitive), e.g. "Size (L x W x H)".
 *
 * For each matching `.option-selector`:
 *   1. Logs the currently selected option's index on first sight.
 *   2. Logs the newly selected option's index every time it changes.
 *
 * Handles both variant-picker layouts:
 *   - Listed layout: `<fieldset class="option-selector">` with
 *     `<input class="opt-btn js-option">` radios. Detected via the bubbling
 *     `change` event.
 *   - Dropdown layout: `<div class="option-selector">` with a `<custom-select>`
 *     child that toggles `aria-selected="true"` on its options. Detected via a
 *     scoped `MutationObserver` watching `aria-selected` mutations.
 *
 * Attach-once via a WeakSet so section AJAX swaps that reinsert the picker
 * don't produce duplicate listeners.
 */
(function () {
    "use strict";

    // Guard against the script being loaded twice on the same page.
    if (window.__briffaSizeOptionLoggerInstalled) return;
    window.__briffaSizeOptionLoggerInstalled = true;

    var attached = new WeakSet();

    /** CSS selector for "any option group whose data-option contains 'Size'". */
    var SIZE_SELECTOR = '.option-selector[data-option*="Size" i]';

    /**
     * Vendors for which the `on:size-option:change` custom event is
     * dispatched. Products from other vendors still receive the console
     * log (useful for diagnostics) but do not trigger the scale effect.
     * Vendor comes from the `data-vendor` attribute on the enclosing
     * <variant-picker>, populated from `product.vendor` in variant-picker.liquid.
     */
    var ALLOWED_VENDORS = ["Juliani Collection MO", "LED Mansion"];

    function isVendorAllowed(selector) {
        var picker = selector.closest ? selector.closest("variant-picker") : null;
        if (!picker) return false;
        var vendor = picker.getAttribute("data-vendor") || "";
        for (var i = 0; i < ALLOWED_VENDORS.length; i++) {
            if (vendor === ALLOWED_VENDORS[i]) return true;
        }
        return false;
    }

    /**
     * Reads the `enableVariantResponsiveSizing` feature flag from the
     * enclosing <variant-picker>'s `data-enable-variant-responsive-sizing`
     * attribute. Set by the hosting section (e.g. main-product.liquid)
     * via the variant-picker snippet's `enable_variant_responsive_sizing`
     * parameter. Defaults to false when the attribute is absent so
     * callers that don't opt in get no resize side-effects.
     */
    function isFeatureEnabled(selector) {
        var picker = selector.closest ? selector.closest("variant-picker") : null;
        if (!picker) return false;
        return picker.getAttribute("data-enable-variant-responsive-sizing") === "true";
    }

    /**
     * Returns an array of option elements for the given option-selector,
     * regardless of layout. Listed layout returns the `<input>` radios;
     * dropdown layout returns the `.custom-select__option` elements.
     */
    function optionsIn(selector) {
        var inputs = selector.querySelectorAll("input.opt-btn.js-option");
        if (inputs.length > 0) return inputs;
        return selector.querySelectorAll(".custom-select__option");
    }

    /** Returns the total number of options in the selector. */
    function optionCount(selector) {
        return optionsIn(selector).length;
    }

    /**
     * Returns the index of the currently selected value within the given
     * option-selector, or -1 if nothing is selected. Works for both listed
     * (radio) and dropdown (custom-select) layouts.
     */
    function selectedIndex(selector) {
        var opts = optionsIn(selector);
        for (var i = 0; i < opts.length; i++) {
            var el = opts[i];
            if (el.tagName === "INPUT") {
                if (el.checked) return i;
            } else {
                if (el.getAttribute("aria-selected") === "true") return i;
            }
        }
        return -1;
    }

    /** Returns the value string of the option at `index`, or null. */
    function valueAt(selector, index) {
        if (index < 0) return null;
        var opts = optionsIn(selector);
        var el = opts[index];
        if (!el) return null;
        if (el.tagName === "INPUT") return el.value;
        return el.dataset.value || (el.textContent || "").trim() || null;
    }

    /** Percentage step subtracted for each index below the maximum. */
    var STEP = 15;

    /**
     * Computes `100 - ((max_index - active_index) * STEP)` where
     * max_index = count - 1. Returns null when no option is selected or
     * the group is empty.
     *
     * Examples with count=5 (max_index=4) and STEP=15:
     *   active=0 → 40, active=1 → 55, active=2 → 70, active=3 → 85, active=4 → 100
     */
    function scaledValue(count, activeIndex) {
        if (count <= 0 || activeIndex < 0) return null;
        var maxIndex = count - 1;
        return 100 - (maxIndex - activeIndex) * STEP;
    }

    /**
     * Dispatches a `on:size-option:change` event on `document` carrying the
     * current selection state. Subscribers (e.g. size-option-scale-target.js)
     * can react to this without knowing anything about the picker DOM.
     *
     * detail: {
     *   name:   the option label (e.g. "Size (L x W x H)")
     *   index:  0-based index of the selected option, or -1 if none
     *   count:  total number of options in the group
     *   value:  the selected option's raw value string, or null
     *   scaled: 100 - ((count - 1 - index) * 5), or null
     *   scale:  scaled / 100 (a 0–1 multiplier for transform: scale()), or null
     *   phase:  "initial" on first sight, "changed" on subsequent updates
     * }
     */
    function dispatch(phase, name, selector) {
        // Only products from the allowed vendors participate in the
        // custom-event pipeline; everything else is a no-op.
        if (!isVendorAllowed(selector)) return;

        // Feature-flag gate: hosting sections opt in by setting
        // `enable_variant_responsive_sizing: true` on the variant-picker
        // render. When the flag is off (or absent), no event is dispatched,
        // which transparently disables every resize consumer that keys off
        // `on:size-option:change` (scale target + carousel height trackers).
        if (!isFeatureEnabled(selector)) return;

        var idx = selectedIndex(selector);
        var count = optionCount(selector);
        var val = valueAt(selector, idx);
        var scaled = scaledValue(count, idx);
        var scale = scaled == null ? null : scaled / 100;
        document.dispatchEvent(
            new CustomEvent("on:size-option:change", {
                detail: {
                    name: name,
                    index: idx,
                    count: count,
                    value: val,
                    scaled: scaled,
                    scale: scale,
                    phase: phase,
                },
            })
        );
    }

    function log(action, name, selector) {
        var idx = selectedIndex(selector);
        var count = optionCount(selector);
        var val = valueAt(selector, idx);
        var scaled = scaledValue(count, idx);
        console.log(
            "[size-option] " +
                action +
                ' — "' +
                name +
                '" index=' +
                idx +
                " count=" +
                count +
                " value=" +
                JSON.stringify(val) +
                " scaled=" +
                scaled
        );
        dispatch(action, name, selector);
    }

    function attach(selector) {
        if (attached.has(selector)) return;
        attached.add(selector);

        var name = selector.getAttribute("data-option") || "(unnamed)";
        log("initial", name, selector);

        // Radio-based (listed) pickers dispatch a bubbling `change` event.
        selector.addEventListener("change", function () {
            log("changed", name, selector);
        });

        // Dropdown pickers (custom-select) toggle aria-selected on option
        // elements — watch attribute mutations scoped to this selector.
        var observer = new MutationObserver(function (mutations) {
            for (var i = 0; i < mutations.length; i++) {
                if (
                    mutations[i].type === "attributes" &&
                    mutations[i].attributeName === "aria-selected"
                ) {
                    log("changed", name, selector);
                    return;
                }
            }
        });
        observer.observe(selector, {
            subtree: true,
            attributes: true,
            attributeFilter: ["aria-selected"],
        });
    }

    function scan(root) {
        (root || document).querySelectorAll(SIZE_SELECTOR).forEach(attach);
    }

    // Defer the initial scan until DOMContentLoaded so any subscriber
    // scripts loaded via `defer` after this one (they execute in document
    // order, before DOMContentLoaded) have had a chance to register their
    // listeners. Without this, the "initial" event fires synchronously
    // before subscribers exist and gets dropped.
    if (document.readyState === "complete") {
        scan();
    } else {
        document.addEventListener(
            "DOMContentLoaded",
            function () {
                scan();
            },
            {once: true}
        );
    }

    // Re-scan when the theme swaps sections/pickers via AJAX (quickbuy,
    // section rendering, etc.) so newly-inserted size options are wired up.
    new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
            var added = mutations[i].addedNodes;
            for (var j = 0; j < added.length; j++) {
                var n = added[j];
                if (n.nodeType !== 1) continue;
                if (n.matches && n.matches(SIZE_SELECTOR)) attach(n);
                else scan(n);
            }
        }
    }).observe(document.body, {subtree: true, childList: true});
})();
